import React, { useCallback, useEffect, useRef, useState } from 'react';
import { EditorState } from 'draft-js';
import { FONT_SIZES } from '../../constants/constants';
import { buildFragment, htmlToStyledFragment, insertFragment, sameStyles, withoutOwnStyles } from './pasteHtml';
import PasteChoice, { PASTE_KEPT, PASTE_TEXT_ONLY } from './PasteChoice';
import { isTextOnlyPasteShortcut } from '../../shared/paste/pasteChoice';

// Collage riche (Word, Google Docs, web) d'un éditeur draft-js : garde gras,
// italique, souligné et les couleurs de la palette du document. Sans HTML
// exploitable, draft-js colle le texte brut (stripPastedStyles) avec les
// styles du curseur.
//
// « Conserver uniquement le texte » : par Mod + Maj + V (Mod + Option + Maj + V
// sous Safari), ou après coup avec la pastille affichée sous `anchorRef`.
// - `write` : pose le nouvel état de l'éditeur ;
// - `active` : l'éditeur est en cours d'édition (sinon la pastille disparaît) ;
// - `colors`, `highlightColors` : palettes du document ;
// - `defaultColor` (label ou { style }) et `defaultFontSize` : rendu du texte
//   hors style posé, non repris en style.
// Rend `handlePastedText` (prop de l'Editor), `onKeyDown` et `onKeyUp` (à poser
// sur un parent de l'Editor) et `pasteChoice` (élément à rendre).
export const useStyledPaste = ({
  editorState,
  write,
  anchorRef,
  active,
  colors,
  highlightColors,
  defaultColor,
  defaultFontSize,
}) => {
  // Posé au keydown du raccourci, lu par le collage qui suit, effacé au keyup.
  const textOnlyPasteRef = useRef(false);
  // Dernier collage mis en forme : contenu avant collage et ses deux variantes.
  const [choice, setChoice] = useState(null);

  const handlePastedText = useCallback(
    (text, html, currentEditorState) => {
      const textOnly = textOnlyPasteRef.current;
      textOnlyPasteRef.current = false;
      setChoice(null);
      const blocks = htmlToStyledFragment(html, {
        colors,
        defaultColor: (typeof defaultColor === 'string'
          ? colors.find(({ label }) => label === defaultColor)
          : defaultColor)?.style,
        highlightColors,
        fontSizes: FONT_SIZES.map(({ value }) => value),
        defaultFontSize,
      });
      if (!blocks) return 'not-handled';

      const content = currentEditorState.getCurrentContent();
      const selection = currentEditorState.getSelection();
      const currentBlock = content.getBlockForKey(selection.getStartKey());
      const cursorStyles = currentEditorState.getCurrentInlineStyle().toArray();
      const paste = (pastedBlocks, baseStyles) => buildFragment(
        pastedBlocks,
        baseStyles,
        currentBlock.getType(),
        currentBlock.getDepth()
      );

      // Texte seul : tous les styles du curseur, listes de la source gardées.
      const textOnlyFragment = paste(withoutOwnStyles(blocks), cursorStyles);
      const textOnlyContent = insertFragment(content, selection, textOnlyFragment);
      if (textOnly) {
        write(EditorState.push(currentEditorState, textOnlyContent, 'insert-fragment'));
        return 'handled';
      }

      // Styles du curseur gardés (taille, police) ; ceux que le collage porte
      // lui-même sont remplacés par les siens.
      const pastedStyles = new Set(['BOLD', 'ITALIC', 'UNDERLINE', ...colors.map(({ label }) => label)]);
      const fragment = paste(
        blocks,
        cursorStyles.filter((style) => !pastedStyles.has(style) && !style.startsWith('HIGHLIGHT'))
      );
      const contentAfter = insertFragment(content, selection, fragment);
      write(EditorState.push(currentEditorState, contentAfter, 'insert-fragment'));
      if (!sameStyles(fragment, textOnlyFragment)) {
        setChoice({
          before: currentEditorState,
          [PASTE_KEPT]: contentAfter,
          [PASTE_TEXT_ONLY]: textOnlyContent,
          value: PASTE_KEPT,
        });
      }
      return 'handled';
    },
    [colors, highlightColors, defaultColor, defaultFontSize, write]
  );

  // Rejoue le collage depuis l'état d'avant : une seule étape d'annulation.
  const choose = useCallback((value) => {
    write(EditorState.push(choice.before, choice[value], 'insert-fragment'));
    setChoice({ ...choice, value });
  }, [choice, write]);

  // Toute autre modification du texte, ou la fin de l'édition, retire la
  // pastille.
  useEffect(() => {
    if (!choice) return;
    if (!active || editorState.getCurrentContent() !== choice[choice.value]) {
      setChoice(null);
    }
  }, [editorState, active, choice]);

  const onKeyDown = useCallback((e) => {
    textOnlyPasteRef.current = isTextOnlyPasteShortcut(e);
  }, []);
  const onKeyUp = useCallback(() => {
    textOnlyPasteRef.current = false;
  }, []);

  const pasteChoice = choice && (
    <PasteChoice
      anchorRef={anchorRef}
      content={editorState.getCurrentContent()}
      value={choice.value}
      onChange={choose}
    />
  );

  return { handlePastedText, onKeyDown, onKeyUp, pasteChoice };
};
